// Индекс документа: плоский список блоков с id, позицией, текстом и номером - для панелей (структура,
// находки, выбор ссылки), живой проверки (lint) и перехода к узлу.
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/core';
import { TextSelection, NodeSelection } from '@tiptap/pm/state';
import type { Numbering, NumInfo } from './numbering';
import type { Meta, Source } from './types';
import { numberingStore } from './editor/numberingPlugin';
import { appStore } from './store';

export interface IndexEntry {
  id: string;
  type: string;
  pos: number;
  size: number;
  text: string;
  attrs: Record<string, any>;
  depth: number;
  /** id приложения, внутри которого узел */
  appendix: string | null;
  num?: NumInfo;
  node: PMNode;
}

export interface DocIndex {
  entries: IndexEntry[];
  byId: Map<string, IndexEntry>;
  numbering: Numbering;
  doc: PMNode;
  meta: Meta | null;
  sources: Source[];
}

export function buildIndex(doc: PMNode, numbering: Numbering, meta: Meta | null, sources: Source[]): DocIndex {
  const entries: IndexEntry[] = [];
  const byId = new Map<string, IndexEntry>();
  let appendix: string | null = null;
  let appEnd = -1;
  doc.descendants((node, pos, _parent) => {
    if (pos >= appEnd) appendix = null;
    const id = node.attrs?.id as string | undefined;
    if (node.type.name === 'appendix') { appendix = id || null; appEnd = pos + node.nodeSize; }
    if (id && node.type.name !== 'footnote') {
      const e: IndexEntry = {
        id, type: node.type.name, pos, size: node.nodeSize, text: node.textContent, attrs: node.attrs,
        depth: doc.resolve(pos).depth, appendix, num: numbering.byId.get(id), node,
      };
      entries.push(e);
      byId.set(id, e);
    }
    return !node.isAtom;
  });
  return { entries, byId, numbering, doc, meta, sources };
}

/** Индекс текущего документа редактора. */
export function currentIndex(editor: Editor): DocIndex {
  const s = appStore.getState();
  return buildIndex(editor.state.doc, numberingStore.get(), s.meta, s.sources);
}

/** Перейти к узлу по id: выделить, прокрутить, подсветить. Возвращает false, если узла нет. */
export function gotoNode(editor: Editor, id: string): boolean {
  let found = -1;
  let node: PMNode | null = null;
  editor.state.doc.descendants((n, pos) => {
    if (found >= 0) return false;
    if (n.attrs?.id === id) { found = pos; node = n; return false; }
    return true;
  });
  if (found < 0 || !node) return false;
  const n = node as PMNode;
  const tr = editor.state.tr;
  if (n.isAtom || n.type.name === 'figure' || n.type.name === 'listing') tr.setSelection(NodeSelection.create(editor.state.doc, found));
  else tr.setSelection(TextSelection.near(editor.state.doc.resolve(Math.min(found + 1, editor.state.doc.content.size))));
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
  const dom = editor.view.nodeDOM(found) as HTMLElement | null;
  if (dom && dom.scrollIntoView) {
    dom.scrollIntoView({ block: 'center', behavior: 'smooth' });
    dom.classList.add('sto-flash');
    setTimeout(() => dom.classList.remove('sto-flash'), 1600);
  }
  appStore.getState().set({ focusNode: id });
  return true;
}
